import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOnboardingStore } from '../../stores/useOnboardingStore';
import { tourDefinitions } from './tours';
import type { TourStep, Placement } from '../../models/tour.interfaces';
import styles from './TourEngine.module.css';

interface TooltipPosition {
  top: number;
  left: number;
  placement?: Placement;
}

const PADDING = 8;
const TOOLTIP_OFFSET = 16;

// Map tour modules to their expected route prefixes
const MODULE_ROUTE_MAP: Record<string, string> = {
  employees: '/empleados',
  services: '/servicios',
  products: '/almacen',
  customers: '/clientes',
  reservations: '/reservas',
  pos: '/cajas',
};

const getTooltipPosition = (
  targetRect: DOMRect,
  placement: Placement,
  tooltipEl: HTMLElement | null,
): TooltipPosition => {
  const tooltipWidth = tooltipEl?.offsetWidth || 320;
  const tooltipHeight = tooltipEl?.offsetHeight || 180;

  let top = 0;
  let left = 0;

  // Si el lado pedido no tiene espacio, voltear al opuesto para que el cuadro
  // nunca quede encima del objetivo (el clamp al viewport lo taparía).
  const vwEarly = window.innerWidth;
  const vhEarly = window.innerHeight;
  if (placement === 'top' && targetRect.top - tooltipHeight - TOOLTIP_OFFSET < 10) placement = 'bottom';
  else if (placement === 'bottom' && targetRect.bottom + TOOLTIP_OFFSET + tooltipHeight > vhEarly - 10) placement = 'top';
  else if (placement === 'left' && targetRect.left - tooltipWidth - TOOLTIP_OFFSET < 10) placement = 'right';
  else if (placement === 'right' && targetRect.right + TOOLTIP_OFFSET + tooltipWidth > vwEarly - 10) placement = 'left';

  switch (placement) {
    case 'bottom':
      top = targetRect.bottom + TOOLTIP_OFFSET;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      break;
    case 'top':
      top = targetRect.top - tooltipHeight - TOOLTIP_OFFSET;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      break;
    case 'right':
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      left = targetRect.right + TOOLTIP_OFFSET;
      break;
    case 'left':
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      left = targetRect.left - tooltipWidth - TOOLTIP_OFFSET;
      break;
  }

  // Keep within viewport
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (left < 10) left = 10;
  if (left + tooltipWidth > vw - 10) left = vw - tooltipWidth - 10;
  if (top < 10) top = 10;
  if (top + tooltipHeight > vh - 10) top = vh - tooltipHeight - 10;

  return { top, left, placement };
};

const TourEngine = () => {
  const activeTour = useOnboardingStore((s) => s.activeTour);
  const activeTourStep = useOnboardingStore((s) => s.activeTourStep);
  const advanceTourStep = useOnboardingStore((s) => s.advanceTourStep);
  const previousTourStep = useOnboardingStore((s) => s.previousTourStep);
  const completeTour = useOnboardingStore((s) => s.completeTour);
  const skipTour = useOnboardingStore((s) => s.skipTour);
  const endTour = useOnboardingStore((s) => s.endTour);
  const dynamicDef = useOnboardingStore((s) => s.activeTourDefinition);
  const endDynamicTour = useOnboardingStore((s) => s.endDynamicTour);
  const isDynamic = !!dynamicDef;
  const isAdri = dynamicDef?.variant === 'adri';

  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition>({ top: 0, left: 0 });
  const [stepCompleted, setStepCompleted] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const completionObserverRef = useRef<MutationObserver | null>(null);
  const completionIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPathRef = useRef(window.location.pathname);

  const tourDef = dynamicDef ?? (activeTour ? tourDefinitions[activeTour] : null);
  const tourActive = !!tourDef;
  const finishTour = useCallback(() => {
    if (useOnboardingStore.getState().activeTourDefinition) endDynamicTour('finish');
    else if (activeTour) completeTour(activeTour);
  }, [activeTour, completeTour, endDynamicTour]);
  const currentStep: TourStep | null =
    tourDef && activeTourStep < tourDef.steps.length
      ? tourDef.steps[activeTourStep]
      : null;

  const isLastStep = tourDef ? activeTourStep >= tourDef.steps.length - 1 : false;

  // ================================================================
  // BUG FIX #2: Cancel tour when user navigates to a different page
  // ================================================================
  useEffect(() => {
    if (!activeTour && !dynamicDef) return;

    const expectedRoute = dynamicDef
      ? dynamicDef.routePrefix
      : MODULE_ROUTE_MAP[activeTour!];
    if (!expectedRoute) return;

    const checkRoute = () => {
      const currentPath = window.location.pathname;
      if (currentPath !== lastPathRef.current) {
        lastPathRef.current = currentPath;
        if (!currentPath.startsWith(expectedRoute)) {
          if (dynamicDef) endDynamicTour('route-change');
          else endTour();
        }
      }
    };

    // Listen for popstate (back/forward) and click-based navigation
    window.addEventListener('popstate', checkRoute);
    const routeInterval = setInterval(checkRoute, 300);

    return () => {
      window.removeEventListener('popstate', checkRoute);
      clearInterval(routeInterval);
    };
  }, [activeTour, dynamicDef, endTour, endDynamicTour]);

  const findAndHighlightTarget = useCallback(() => {
    if (!currentStep) return;

    const selector = currentStep.target;
    const el = document.querySelector(selector) as HTMLElement | null;

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });

      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        setTargetRect(rect);
        setTooltipPos(
          getTooltipPosition(rect, currentStep.placement, tooltipRef.current),
        );
      });
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  // Find target when step changes
  useEffect(() => {
    if (!currentStep) return;

    // Reset completion state on step change
    setStepCompleted(false);

    // Clear any pending auto-advance timer
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }

    // Initial attempt
    findAndHighlightTarget();

    // Retry with delay for dynamically loaded elements
    const retryTimer = setTimeout(findAndHighlightTarget, 500);
    const retryTimer2 = setTimeout(findAndHighlightTarget, 1500);
    // Guías dinámicas: si tras los reintentos el objetivo no existe, se sale
    const missingTimer = useOnboardingStore.getState().activeTourDefinition
      ? setTimeout(() => {
          if (document.querySelector(currentStep.target)) return;
          const st = useOnboardingStore.getState();
          if (!currentStep.optional || !st.activeTourDefinition) {
            st.endDynamicTour('target-missing');
            return;
          }
          // Paso opcional ausente: salta al siguiente cuyo objetivo exista o que no sea opcional
          const steps = st.activeTourDefinition.steps;
          const from = st.activeTourStep;
          let next = from + 1;
          while (next < steps.length && steps[next].optional && !document.querySelector(steps[next].target)) next++;
          if (next >= steps.length) {
            st.endDynamicTour('finish');
            return;
          }
          for (let i = from; i < next; i++) useOnboardingStore.getState().advanceTourStep();
        }, 2500)
      : null;

    // Observe DOM changes
    observerRef.current = new MutationObserver(() => {
      findAndHighlightTarget();
    });
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(retryTimer);
      clearTimeout(retryTimer2);
      if (missingTimer) clearTimeout(missingTimer);
      observerRef.current?.disconnect();
    };
  }, [currentStep, findAndHighlightTarget]);

  // Auto-advance when target disappears (for modal steps)
  useEffect(() => {
    if (!currentStep || currentStep.nextStepTrigger !== 'onTargetDisappear') return;

    let targetWasFound = false;

    const isElementVisible = (el: Element): boolean => {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
      let parent = el.parentElement;
      while (parent && parent !== document.body) {
        const ps = window.getComputedStyle(parent);
        if (ps.display === 'none' || ps.visibility === 'hidden') return false;
        parent = parent.parentElement;
      }
      return true;
    };

    const checkDisappearance = () => {
      const el = document.querySelector(currentStep.target);
      const visible = el ? isElementVisible(el) : false;
      if (visible) {
        targetWasFound = true;
      } else if (targetWasFound) {
        if (isLastStep) {
          finishTour();
        } else {
          advanceTourStep();
        }
      }
    };

    const disappearObserver = new MutationObserver(checkDisappearance);
    disappearObserver.observe(document.body, { childList: true, subtree: true });
    const pollInterval = setInterval(checkDisappearance, 500);
    checkDisappearance();

    return () => {
      disappearObserver.disconnect();
      clearInterval(pollInterval);
    };
  }, [currentStep, isLastStep, advanceTourStep, finishTour]);

  // Check step completion
  useEffect(() => {
    if (!currentStep?.completionCheck) return;

    const checkCompletion = () => {
      const el = document.querySelector(currentStep.target) as HTMLElement | null;
      if (!el) return;

      const isComplete = currentStep.completionCheck!(el);
      if (isComplete && !stepCompleted) {
        setStepCompleted(true);
      }
    };

    const targetEl = document.querySelector(currentStep.target);
    if (targetEl) {
      completionObserverRef.current = new MutationObserver(checkCompletion);
      completionObserverRef.current.observe(targetEl, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }

    completionIntervalRef.current = setInterval(checkCompletion, 800);
    checkCompletion();

    return () => {
      completionObserverRef.current?.disconnect();
      if (completionIntervalRef.current) {
        clearInterval(completionIntervalRef.current);
      }
    };
  }, [currentStep, stepCompleted]);

  // ================================================================
  // BUG FIX #3: Auto-advance after step completion (with delay)
  // ================================================================
  useEffect(() => {
    if (!stepCompleted || !currentStep?.completedBody) return;
    if (currentStep.nextStepTrigger === 'onFinish' || isLastStep) return;

    autoAdvanceTimerRef.current = setTimeout(() => {
      advanceTourStep();
    }, 2000);

    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    };
  }, [stepCompleted, currentStep, isLastStep, advanceTourStep]);

  // Hide elements specified by hideSelectors
  useEffect(() => {
    if (!currentStep?.hideSelectors?.length) return;

    const hidden: { el: HTMLElement; prev: string }[] = [];

    const hideElements = () => {
      currentStep.hideSelectors!.forEach((selector) => {
        const el = document.querySelector(selector) as HTMLElement | null;
        if (el && !hidden.some((h) => h.el === el)) {
          hidden.push({ el, prev: el.style.display });
          el.style.display = 'none';
        }
      });
    };

    hideElements();
    const t1 = setTimeout(hideElements, 300);
    const t2 = setTimeout(hideElements, 1000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      hidden.forEach(({ el, prev }) => {
        el.style.display = prev;
      });
    };
  }, [currentStep]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!tourActive) return;

    const handleReposition = () => findAndHighlightTarget();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [tourActive, findAndHighlightTarget]);

  // Make target element interactive and handle click trigger
  useEffect(() => {
    if (!currentStep) return;

    let attachedEl: HTMLElement | null = null;
    let clickHandler: (() => void) | null = null;
    const elevatedAncestors: { el: HTMLElement; prevZ: string }[] = [];

    const attachToElement = () => {
      if (attachedEl) return;

      const el = document.querySelector(currentStep.target) as HTMLElement | null;
      if (!el) return;

      attachedEl = el;

      // Check if target is inside a modal/overlay
      let ancestor = el.parentElement;
      while (ancestor && ancestor !== document.body) {
        const computedZ = window.getComputedStyle(ancestor).zIndex;
        if (computedZ !== 'auto' && parseInt(computedZ) > 0) {
          if (parseInt(computedZ) < 1100) {
            elevatedAncestors.push({ el: ancestor, prevZ: ancestor.style.zIndex });
            ancestor.style.zIndex = '1100';
          }
        }
        ancestor = ancestor.parentElement;
      }

      // Make target interactive above the blocking overlay
      el.style.position = el.style.position || 'relative';
      el.style.zIndex = '1100';
      el.style.pointerEvents = 'auto';

      if (currentStep.nextStepTrigger === 'onElementClick') {
        clickHandler = () => {
          if (isLastStep) {
            finishTour();
          } else {
            advanceTourStep();
          }
        };
        el.addEventListener('click', clickHandler);
      }
    };

    attachToElement();
    const t1 = setTimeout(attachToElement, 500);
    const t2 = setTimeout(attachToElement, 1500);

    const interactObserver = new MutationObserver(attachToElement);
    interactObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      interactObserver.disconnect();
      if (attachedEl) {
        if (clickHandler) {
          attachedEl.removeEventListener('click', clickHandler);
        }
        attachedEl.style.zIndex = '';
        attachedEl.style.position = '';
        attachedEl.style.pointerEvents = '';
      }
      elevatedAncestors.forEach(({ el: anc, prevZ }) => {
        anc.style.zIndex = prevZ;
      });
    };
  }, [currentStep, isLastStep, advanceTourStep, finishTour]);

  if (!tourDef || !currentStep) return null;

  const handleNext = () => {
    if (currentStep.nextStepTrigger === 'onFinish' || isLastStep) {
      finishTour();
    } else {
      advanceTourStep();
    }
  };

  // ================================================================
  // BUG FIX #5: Safe previous step - skip back past steps whose target
  // doesn't exist in the current DOM (e.g. navigated away from a modal)
  // ================================================================
  const handlePrevious = () => {
    if (!tourDef || activeTourStep <= 0) return;

    let targetStep = activeTourStep - 1;
    while (targetStep >= 0) {
      const step = tourDef.steps[targetStep];
      const el = document.querySelector(step.target);
      if (el) break;
      targetStep--;
    }

    if (targetStep < 0) targetStep = 0;

    // Use the store's set directly for arbitrary step jump
    const diff = activeTourStep - targetStep;
    for (let i = 0; i < diff; i++) {
      previousTourStep();
    }
  };

  const handleSkip = () => {
    skipTour(activeTour!);
  };

  const handleClose = () => {
    if (isDynamic) endDynamicTour('user');
    else endTour();
  };

  const effectivePlacement = tooltipPos.placement ?? currentStep.placement;
  const arrowClass =
    effectivePlacement === 'bottom'
      ? styles.tooltipArrowBottom
      : effectivePlacement === 'top'
        ? styles.tooltipArrowTop
        : effectivePlacement === 'left'
          ? styles.tooltipArrowLeft
          : styles.tooltipArrowRight;

  const canAdvanceWithButton =
    currentStep.nextStepTrigger === 'onNextButton' ||
    currentStep.nextStepTrigger === 'onTargetDisappear' ||
    currentStep.nextStepTrigger === 'onFinish' ||
    currentStep.requiredAction === 'none' ||
    currentStep.requiredAction.includes('optional');

  const displayBody = stepCompleted && currentStep.completedBody
    ? currentStep.completedBody
    : currentStep.body;

  return createPortal(
    <>
      {/* Tour visual layer — non-blocking. The user can interact freely with the
          underlying app (the spotlight box-shadow gives the dim look). */}
      <div className={styles.blockingOverlay} />

      {/* Spotlight overlay */}
      {targetRect && (
        <div
          className={styles.spotlight}
          style={{
            top: targetRect.top - PADDING,
            left: targetRect.left - PADDING,
            width: targetRect.width + PADDING * 2,
            height: targetRect.height + PADDING * 2 + (currentStep?.spotlightExtraHeight || 0),
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`${styles.tooltip} ${isAdri ? styles.adri : ''}`}
        data-testid={isAdri ? 'adri-guide-tooltip' : undefined}
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
        }}
      >
        <div className={`${styles.tooltipArrow} ${arrowClass}`} />

        {isAdri && (
          <div className={styles.adriHeader}>
            <span className={styles.adriAvatar} data-testid="adri-guide-avatar" aria-hidden="true">
              A
            </span>
            <span className={styles.adriLabel}>
              Adri te guía · Paso {activeTourStep + 1} de {tourDef.steps.length}
            </span>
          </div>
        )}

        <div className={styles.tooltipHeader}>
          <h4 className={styles.tooltipTitle}>
            {stepCompleted && currentStep.completedBody ? '✓ ' : ''}{currentStep.title}
          </h4>
          <button
            className={styles.tooltipCloseBtn}
            onClick={handleClose}
            aria-label="Cerrar tour"
          >
            &times;
          </button>
        </div>

        <p className={`${styles.tooltipBody} ${stepCompleted && currentStep.completedBody ? styles.tooltipBodyCompleted : ''}`}>
          {displayBody}
        </p>

        <div className={styles.tooltipFooter}>
          <span className={styles.tooltipStepCounter}>
            {isAdri ? '' : `${activeTourStep + 1} / ${tourDef.steps.length}`}
          </span>

          <div className={styles.tooltipActions}>
            {isAdri && (
              <button className={styles.btnSkip} onClick={() => endDynamicTour('user')}>
                Salir de la guía
              </button>
            )}

            {!isAdri && currentStep.skippable && (
              <button className={styles.btnSkip} onClick={handleSkip}>
                Saltar tour
              </button>
            )}

            {!isAdri && activeTourStep > 0 && (
              <button className={styles.btnPrev} onClick={handlePrevious}>
                Anterior
              </button>
            )}

            {canAdvanceWithButton && (
              <button
                className={`${styles.btnNext} ${stepCompleted && currentStep.completedBody ? styles.btnNextPulse : ''}`}
                onClick={handleNext}
              >
                {isLastStep || currentStep.nextStepTrigger === 'onFinish'
                  ? 'Finalizar'
                  : 'Siguiente'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default TourEngine;
